import { useEffect, useState, useMemo } from "react";
import { Plus, Pencil, Trash2, Search, PackageX, PackageCheck, ChevronLeft, ChevronRight, Copy, Download, LayoutGrid, Rows3, Star, Check, X, RotateCcw } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MultiImageUpload } from "@/components/MultiImageUpload";
import { toast } from "sonner";

const ROOMS = ["salon", "cuisine", "chambre", "salle-de-bain", "exterieur", "pro"];
const ROOM_LABELS = {
  "salon": "Salon",
  "cuisine": "Cuisine",
  "chambre": "Chambre",
  "salle-de-bain": "Salle de bain",
  "exterieur": "Extérieur",
  "pro": "Professionnel",
};
const COLOR_TEMPS = [
  { value: "chaud", label: "Blanc chaud (2700–3000 K)" },
  { value: "neutre", label: "Blanc neutre (4000 K)" },
  { value: "froid", label: "Blanc froid (5000–6500 K)" },
];

const EMPTY = {
  name: "", description: "", short_description: "",
  price: "", original_price: "", category_slug: "",
  type: "", style: "", brand: "", image: "", images: [],
  stock: 25, featured: false, active: true, rooms: [], weight: "",
  sku: "", lumens: "", color_temp: "", dimmable: false, wattage: "",
  ip_rating: "", dimensions: "", material: "", finish: "",
  colors_available: [], tags: [],
};

const Lbl = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>
);

function Tabs({ tabs, active, onChange }) {
  return (
    <div className="border-b border-stone flex flex-wrap gap-1 -mt-2 mb-4">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          data-testid={`admin-product-tab-${t.id}`}
          className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] -mb-px border-b-2 transition-colors ${
            active === t.id
              ? "border-copper text-ink"
              : "border-transparent text-ash hover:text-ink"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // visibilité: all|active|inactive
  const [stockFilter, setStockFilter] = useState("all");    // stock: all|instock|low|out
  const [styleFilter, setStyleFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [promoOnly, setPromoOnly] = useState(false);
  const [sortBy, setSortBy] = useState("name-asc");
  const [viewMode, setViewMode] = useState("list"); // list | grid
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("general");
  const [colorsInput, setColorsInput] = useState("");
  const [stockBusy, setStockBusy] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [editStockId, setEditStockId] = useState(null);
  const [editStockValue, setEditStockValue] = useState("");
  const PER_PAGE = 24;

  const load = () => {
    setLoading(true);
    api.get("/admin/products").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  const styles = useMemo(
    () => Array.from(new Set(products.map((p) => p.style).filter(Boolean))).sort(),
    [products]
  );
  const brands = useMemo(
    () => Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort(),
    [products]
  );

  const filtered = useMemo(() => {
    const min = priceMin === "" ? null : parseFloat(priceMin);
    const max = priceMax === "" ? null : parseFloat(priceMax);
    let list = products.filter((p) => {
      if (catFilter !== "all" && p.category_slug !== catFilter) return false;
      // Visibilité (En vente / Hors vente) — indépendant du stock
      if (statusFilter === "active" && p.active === false) return false;
      if (statusFilter === "inactive" && p.active !== false) return false;
      // Stock
      const st = p.stock ?? 0;
      if (stockFilter === "instock" && st <= 0) return false;
      if (stockFilter === "low" && !(st > 0 && st <= 3)) return false;
      if (stockFilter === "out" && st > 0) return false;
      // Style / marque
      if (styleFilter !== "all" && p.style !== styleFilter) return false;
      if (brandFilter !== "all" && p.brand !== brandFilter) return false;
      // Prix
      if (min != null && !Number.isNaN(min) && (p.price ?? 0) < min) return false;
      if (max != null && !Number.isNaN(max) && (p.price ?? 0) > max) return false;
      // Chips
      if (featuredOnly && !p.featured) return false;
      if (promoOnly && !(p.original_price && p.original_price > p.price)) return false;
      const hay = `${p.name} ${p.brand || ""} ${p.style || ""} ${p.sku || ""}`.toLowerCase();
      if (search && !hay.includes(search.toLowerCase())) return false;
      return true;
    });
    const cmp = {
      "name-asc": (a, b) => (a.name || "").localeCompare(b.name || ""),
      "name-desc": (a, b) => (b.name || "").localeCompare(a.name || ""),
      "price-asc": (a, b) => (a.price ?? 0) - (b.price ?? 0),
      "price-desc": (a, b) => (b.price ?? 0) - (a.price ?? 0),
      "stock-asc": (a, b) => (a.stock ?? 0) - (b.stock ?? 0),
      "stock-desc": (a, b) => (b.stock ?? 0) - (a.stock ?? 0),
      "recent": (a, b) => (b.imported_at || "").localeCompare(a.imported_at || ""),
    }[sortBy];
    if (cmp) list = [...list].sort(cmp);
    return list;
  }, [products, search, catFilter, statusFilter, stockFilter, styleFilter, brandFilter, priceMin, priceMax, featuredOnly, promoOnly, sortBy]);

  // Statistiques globales (sur tout le catalogue)
  const stats = useMemo(() => {
    const total = products.length;
    const oos = products.filter((p) => (p.stock ?? 0) <= 0).length;
    const low = products.filter((p) => { const s = p.stock ?? 0; return s > 0 && s <= 3; }).length;
    const value = products.reduce((sum, p) => sum + (p.price ?? 0) * (p.stock ?? 0), 0);
    return { total, oos, low, value };
  }, [products]);
  const outOfStockCount = stats.oos;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = useMemo(
    () => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [filtered, page]
  );

  // Reset to page 1 whenever filters/search change
  useEffect(() => {
    setPage(1);
  }, [search, catFilter, statusFilter, stockFilter, styleFilter, brandFilter, priceMin, priceMax, featuredOnly, promoOnly, sortBy]);

  // Clamp page if list shrinks
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const resetFilters = () => {
    setSearch(""); setCatFilter("all"); setStatusFilter("all"); setStockFilter("all");
    setStyleFilter("all"); setBrandFilter("all"); setPriceMin(""); setPriceMax("");
    setFeaturedOnly(false); setPromoOnly(false); setSortBy("name-asc");
  };

  // ---- Sélection multiple (actions groupées) ----
  const pageIds = paged.map((p) => p.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleSelectPage = () =>
    setSelected((s) => (allPageSelected ? s.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...s, ...pageIds]))));
  const clearSelection = () => setSelected([]);

  const applyBulk = async (action) => {
    if (!selected.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk", { ids: selected, action });
      toast.success(`${data.affected} produit(s) — action appliquée`);
      clearSelection();
      setBulkConfirm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const duplicateProduct = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success(`« ${p.name} » dupliqué (copie inactive)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCSV = () => {
    const cols = ["name", "sku", "category", "price", "original_price", "stock", "active", "featured", "style", "brand", "lumens", "dimensions", "slug"];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [cols.join(";")];
    filtered.forEach((p) => rows.push(cols.map((c) => esc(p[c])).join(";")));
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `catalogue-lumiere-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} produits exportés`);
  };

  const startEditStock = (p) => {
    setEditStockId(p.id);
    setEditStockValue(String(p.stock ?? 0));
  };
  const saveEditStock = async (p) => {
    const val = parseInt(editStockValue, 10);
    if (Number.isNaN(val) || val < 0) {
      toast.error("Quantité invalide");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/stock`, { stock: val });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock: data.stock, stock_before_oos: data.stock_before_oos } : x)));
      setEditStockId(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setTab("general");
    setColorsInput("");
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      description: p.description || "",
      short_description: p.short_description || "",
      price: p.price,
      original_price: p.original_price ?? "",
      category_slug: p.category_slug || "",
      type: p.type || "",
      style: p.style || "",
      brand: p.brand ?? "",
      image: p.image || "",
      images: p.images?.length ? [...p.images] : (p.image ? [p.image] : []),
      stock: p.stock ?? 25,
      featured: !!p.featured,
      active: p.active !== false,
      rooms: p.rooms ?? [],
      weight: p.weight ?? "",
      sku: p.sku || "",
      lumens: p.lumens ?? "",
      color_temp: p.color_temp || "",
      dimmable: !!p.dimmable,
      wattage: p.wattage ?? "",
      ip_rating: p.ip_rating || "",
      dimensions: p.dimensions || "",
      material: p.material || "",
      finish: p.finish || "",
      colors_available: p.colors_available ?? [],
      tags: p.tags ?? [],
    });
    setColorsInput((p.colors_available || []).join(", "));
    setTab("general");
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleRoom = (r) =>
    setForm((f) => ({ ...f, rooms: f.rooms.includes(r) ? f.rooms.filter((x) => x !== r) : [...f.rooms, r] }));

  const save = async (e) => {
    e.preventDefault();
    const images = form.images && form.images.length ? form.images : (form.image ? [form.image] : []);
    if (!form.name || !form.category_slug || !form.price || images.length === 0) {
      toast.error("Nom, catégorie, prix et au moins une image sont requis.");
      return;
    }
    setSaving(true);
    const parsedColors = (colorsInput || "")
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean)
      .slice(0, 8);
    const payload = {
      name: form.name,
      description: form.description,
      short_description: form.short_description || null,
      price: parseFloat(form.price),
      original_price: form.original_price === "" ? null : parseFloat(form.original_price),
      category_slug: form.category_slug,
      type: form.type || "luminaire",
      style: form.style || "moderne",
      brand: form.brand || null,
      image: images[0],
      images,
      stock: parseInt(form.stock) || 0,
      featured: form.featured,
      active: form.active,
      rooms: form.rooms,
      weight: form.weight === "" ? null : parseFloat(form.weight),
      sku: form.sku || null,
      lumens: form.lumens === "" ? null : parseInt(form.lumens),
      color_temp: form.color_temp || null,
      dimmable: form.dimmable,
      wattage: form.wattage === "" ? null : parseFloat(form.wattage),
      ip_rating: form.ip_rating || null,
      dimensions: form.dimensions || null,
      material: form.material || null,
      finish: form.finish || null,
      colors_available: parsedColors,
      tags: form.tags,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, {
        ...p,
        active: !(p.active !== false),
      });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: !(x.active !== false) } : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleStock = async (p) => {
    const willBeOut = (p.stock ?? 0) > 0; // si en stock -> on passe en rupture
    setStockBusy(p.id);
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/stock`, { out_of_stock: willBeOut });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock: data.stock, stock_before_oos: data.stock_before_oos } : x)));
      toast.success(willBeOut ? "Produit mis en rupture" : `Remis en stock (${data.stock})`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setStockBusy(null);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/products/${toDelete.id}`);
      toast.success("Produit supprimé");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Catalogue</p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">
            Produits ({filtered.length}{filtered.length !== products.length ? `/${products.length}` : ""})
          </h1>
          {outOfStockCount > 0 && (
            <button
              type="button"
              data-testid="oos-shortcut"
              onClick={() => { setStatusFilter("all"); setStockFilter("out"); }}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-destructive hover:underline"
            >
              <PackageX className="h-3.5 w-3.5" /> {outOfStockCount} produit{outOfStockCount > 1 ? "s" : ""} en rupture — voir
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-stone rounded-sm overflow-hidden" title="Affichage">
            <button type="button" data-testid="view-list" onClick={() => setViewMode("list")} className={`p-2.5 ${viewMode === "list" ? "bg-ink text-cream" : "bg-white text-graphite hover:bg-sand"}`}><Rows3 className="h-4 w-4" /></button>
            <button type="button" data-testid="view-grid" onClick={() => setViewMode("grid")} className={`p-2.5 ${viewMode === "grid" ? "bg-ink text-cream" : "bg-white text-graphite hover:bg-sand"}`}><LayoutGrid className="h-4 w-4" /></button>
          </div>
          <button data-testid="export-csv-btn" onClick={exportCSV} className="flex items-center gap-2 border border-stone bg-white text-graphite px-4 py-3 text-xs uppercase tracking-[0.15em] hover:border-ink hover:text-ink transition-colors">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button data-testid="add-product-btn" onClick={openCreate} className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
            <Plus className="h-4 w-4" /> Ajouter
          </button>
        </div>
      </div>

      {/* Stats banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="products-stats">
        {[
          { label: "Produits", value: stats.total, cls: "text-ink" },
          { label: "En rupture", value: stats.oos, cls: "text-destructive", onClick: () => { setStatusFilter("all"); setStockFilter("out"); } },
          { label: "Stock faible (≤3)", value: stats.low, cls: "text-amber-600", onClick: () => { setStatusFilter("all"); setStockFilter("low"); } },
          { label: "Valeur du stock", value: eur(stats.value), cls: "text-copper" },
        ].map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={s.onClick}
            className={`text-left bg-white border border-stone rounded-sm p-4 ${s.onClick ? "hover:border-ink transition-colors" : "cursor-default"}`}
          >
            <p className="text-[10px] uppercase tracking-[0.15em] text-ash">{s.label}</p>
            <p className={`font-mono text-2xl mt-1 ${s.cls}`}>{s.value}</p>
          </button>
        ))}
      </div>

      {/* Filters — ligne 1 */}
      <div className="flex flex-col md:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-ash absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="product-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, marque, SKU...)" className="pl-9 rounded-sm border-stone bg-white" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="product-cat-filter" className="w-full md:w-40 rounded-sm border-stone bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="product-status-filter" className="w-full md:w-36 rounded-sm border-stone bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Visibilité : toutes</SelectItem>
            <SelectItem value="active">En vente</SelectItem>
            <SelectItem value="inactive">Hors vente</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger data-testid="product-stock-filter" className="w-full md:w-36 rounded-sm border-stone bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Stock : tous</SelectItem>
            <SelectItem value="instock">En stock</SelectItem>
            <SelectItem value="low">Stock faible (≤3)</SelectItem>
            <SelectItem value="out">Rupture</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="product-sort" className="w-full md:w-44 rounded-sm border-stone bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">Nom (A→Z)</SelectItem>
            <SelectItem value="name-desc">Nom (Z→A)</SelectItem>
            <SelectItem value="price-asc">Prix croissant</SelectItem>
            <SelectItem value="price-desc">Prix décroissant</SelectItem>
            <SelectItem value="stock-asc">Stock croissant</SelectItem>
            <SelectItem value="stock-desc">Stock décroissant</SelectItem>
            <SelectItem value="recent">Plus récents</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Filters — ligne 2 (style, marque, prix) */}
      <div className="flex flex-col md:flex-row gap-3 mb-3">
        <Select value={styleFilter} onValueChange={setStyleFilter}>
          <SelectTrigger data-testid="product-style-filter" className="w-full md:w-44 rounded-sm border-stone bg-white text-xs"><SelectValue placeholder="Style" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les styles</SelectItem>
            {styles.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={brandFilter} onValueChange={setBrandFilter}>
          <SelectTrigger data-testid="product-brand-filter" className="w-full md:w-44 rounded-sm border-stone bg-white text-xs"><SelectValue placeholder="Marque" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les marques</SelectItem>
            {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input data-testid="price-min" type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="Prix min €" className="w-full md:w-28 rounded-sm border-stone bg-white text-xs" />
          <span className="text-ash">–</span>
          <Input data-testid="price-max" type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="Prix max €" className="w-full md:w-28 rounded-sm border-stone bg-white text-xs" />
        </div>
        <button type="button" data-testid="reset-filters" onClick={resetFilters} className="flex items-center gap-1.5 text-xs text-ash hover:text-ink px-3 py-2">
          <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
        </button>
      </div>

      {/* Quick chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button type="button" data-testid="chip-rupture" onClick={() => setStockFilter((v) => (v === "out" ? "all" : "out"))} className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 border rounded-full transition-colors ${stockFilter === "out" ? "bg-destructive text-white border-destructive" : "border-stone text-graphite hover:border-ink"}`}>
          <PackageX className="h-3.5 w-3.5" /> En rupture
        </button>
        <button type="button" data-testid="chip-low" onClick={() => setStockFilter((v) => (v === "low" ? "all" : "low"))} className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 border rounded-full transition-colors ${stockFilter === "low" ? "bg-amber-500 text-white border-amber-500" : "border-stone text-graphite hover:border-ink"}`}>
          Stock faible
        </button>
        <button type="button" data-testid="chip-featured" onClick={() => setFeaturedOnly((v) => !v)} className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 border rounded-full transition-colors ${featuredOnly ? "bg-gold text-ink border-gold" : "border-stone text-graphite hover:border-ink"}`}>
          <Star className="h-3.5 w-3.5" /> En vedette
        </button>
        <button type="button" data-testid="chip-promo" onClick={() => setPromoOnly((v) => !v)} className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 border rounded-full transition-colors ${promoOnly ? "bg-copper text-white border-copper" : "border-stone text-graphite hover:border-ink"}`}>
          En promo
        </button>
      </div>

      {/* Légende : différence Vente (visibilité) vs Stock */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-4 text-[11px] text-ash">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" />
          <strong className="text-graphite">En vente / Hors vente</strong> — visibilité en boutique (Hors vente = produit masqué du site)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <PackageX className="h-3.5 w-3.5 text-destructive" />
          <strong className="text-graphite">Rupture</strong> — reste visible mais indisponible, active le formulaire « Prévenez-moi »
        </span>
      </div>

      {/* Barre d'actions groupées */}
      {selected.length > 0 && (
        <div data-testid="bulk-bar" className="flex flex-wrap items-center gap-2 mb-4 bg-ink text-cream px-4 py-3 rounded-sm">
          <span className="text-xs uppercase tracking-[0.15em] mr-2">{selected.length} sélectionné{selected.length > 1 ? "s" : ""}</span>
          <button type="button" data-testid="bulk-oos" disabled={bulkBusy} onClick={() => applyBulk("out_of_stock")} className="text-[11px] uppercase tracking-[0.1em] bg-cream/10 hover:bg-cream/20 px-3 py-1.5 rounded-sm">Mettre en rupture</button>
          <button type="button" data-testid="bulk-instock" disabled={bulkBusy} onClick={() => applyBulk("in_stock")} className="text-[11px] uppercase tracking-[0.1em] bg-cream/10 hover:bg-cream/20 px-3 py-1.5 rounded-sm">Remettre en stock</button>
          <button type="button" data-testid="bulk-activate" disabled={bulkBusy} onClick={() => applyBulk("activate")} className="text-[11px] uppercase tracking-[0.1em] bg-cream/10 hover:bg-cream/20 px-3 py-1.5 rounded-sm">En vente</button>
          <button type="button" data-testid="bulk-deactivate" disabled={bulkBusy} onClick={() => applyBulk("deactivate")} className="text-[11px] uppercase tracking-[0.1em] bg-cream/10 hover:bg-cream/20 px-3 py-1.5 rounded-sm">Hors vente</button>
          <button type="button" data-testid="bulk-feature" disabled={bulkBusy} onClick={() => applyBulk("feature")} className="text-[11px] uppercase tracking-[0.1em] bg-cream/10 hover:bg-cream/20 px-3 py-1.5 rounded-sm">Vedette</button>
          <button type="button" data-testid="bulk-unfeature" disabled={bulkBusy} onClick={() => applyBulk("unfeature")} className="text-[11px] uppercase tracking-[0.1em] bg-cream/10 hover:bg-cream/20 px-3 py-1.5 rounded-sm">Retirer vedette</button>
          <button type="button" data-testid="bulk-delete" disabled={bulkBusy} onClick={() => setBulkConfirm(true)} className="text-[11px] uppercase tracking-[0.1em] bg-destructive hover:bg-destructive/90 px-3 py-1.5 rounded-sm ml-auto">Supprimer</button>
          <button type="button" data-testid="bulk-clear" onClick={clearSelection} className="text-cream/70 hover:text-cream p-1"><X className="h-4 w-4" /></button>
        </div>
      )}

      {viewMode === "list" ? (
      <div className="bg-white border border-stone rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-12 bg-sand animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-no-products" className="p-10 text-center text-graphite">Aucun produit ne correspond.</p>
        ) : (
          <>
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-stone bg-sand/40">
            <Checkbox data-testid="select-all-page" checked={allPageSelected} onCheckedChange={toggleSelectPage} />
            <span className="text-[11px] uppercase tracking-[0.1em] text-ash">Tout sélectionner (page)</span>
          </div>
          <div className="divide-y divide-stone">
            {paged.map((p) => {
              const st = p.stock ?? 0;
              const isOut = st <= 0;
              const low = !isOut && st <= 3;
              const hidden = p.active === false;
              const checked = selected.includes(p.id);
              return (
              <div key={p.id} data-testid={`admin-product-row-${p.slug}`} className={`flex flex-wrap items-center gap-3 p-4 hover:bg-sand/50 transition-opacity ${hidden ? "opacity-60" : ""} ${checked ? "bg-gold/5" : ""}`}>
                <Checkbox data-testid={`select-${p.slug}`} checked={checked} onCheckedChange={() => toggleSelect(p.id)} />
                <img src={p.image} alt={p.name} className="h-14 w-12 object-cover bg-sand rounded-sm shrink-0" />
                <div className="flex-1 min-w-[150px]">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-ink truncate">{p.name}</p>
                    {p.featured && <Star className="h-3 w-3 text-gold fill-gold shrink-0" />}
                  </div>
                  <p className="text-xs text-ash truncate">
                    {p.category} · {p.style}
                    {p.brand ? ` · ${p.brand}` : ""}
                    {p.lumens ? ` · ${p.lumens}lm` : ""}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border ${hidden ? "bg-stone text-ash border-stone" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      {hidden ? "Hors vente" : "En vente"}
                    </span>
                    {editStockId === p.id ? (
                      <span className="inline-flex items-center gap-1">
                        <Input autoFocus type="number" value={editStockValue} onChange={(e) => setEditStockValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEditStock(p); if (e.key === "Escape") setEditStockId(null); }}
                          data-testid={`stock-input-${p.slug}`} className="h-6 w-16 px-1.5 py-0 text-xs rounded-sm border-copper bg-white" />
                        <button type="button" data-testid={`stock-save-${p.slug}`} onClick={() => saveEditStock(p)} className="text-emerald-600 p-0.5"><Check className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => setEditStockId(null)} className="text-ash p-0.5"><X className="h-3.5 w-3.5" /></button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`product-stock-badge-${p.slug}`}
                        onClick={() => startEditStock(p)}
                        title="Cliquer pour modifier la quantité"
                        className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border hover:ring-1 hover:ring-ink/20 ${isOut ? "bg-red-50 text-destructive border-red-200" : low ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-cream text-graphite border-stone"}`}
                      >
                        {isOut ? "Rupture" : low ? `Stock faible · ${st}` : `En stock · ${st}`}
                      </button>
                    )}
                  </div>
                </div>
                <div className="hidden md:flex flex-col items-end w-20 shrink-0">
                  <span className="font-mono text-sm text-ink">{eur(p.price)}</span>
                  {p.original_price ? <span className="font-mono text-[11px] text-ash line-through">{eur(p.original_price)}</span> : null}
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                  <button
                    type="button"
                    data-testid={`stock-toggle-${p.slug}`}
                    disabled={stockBusy === p.id}
                    onClick={() => toggleStock(p)}
                    title={isOut ? "Remettre en stock" : "Mettre en rupture (reste visible + Prévenez-moi)"}
                    className={`flex items-center gap-1.5 px-2.5 py-2 text-[11px] uppercase tracking-[0.1em] border rounded-sm transition-colors disabled:opacity-50 ${isOut ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-stone text-graphite hover:border-destructive hover:text-destructive"}`}
                  >
                    {isOut ? <><PackageCheck className="h-3.5 w-3.5" /> En stock</> : <><PackageX className="h-3.5 w-3.5" /> Rupture</>}
                  </button>
                  <div className="flex items-center pl-1.5 border-l border-stone" title="Visible en boutique (En vente / Hors vente)">
                    <Switch data-testid={`product-active-toggle-${p.slug}`} checked={p.active !== false} onCheckedChange={() => toggleActive(p)} />
                  </div>
                  <button data-testid={`duplicate-product-${p.slug}`} onClick={() => duplicateProduct(p)} title="Dupliquer" className="p-2 text-graphite hover:text-copper"><Copy className="h-4 w-4" /></button>
                  <button data-testid={`edit-product-${p.slug}`} onClick={() => openEdit(p)} className="p-2 text-graphite hover:text-ink"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-product-${p.slug}`} onClick={() => setToDelete(p)} className="p-2 text-graphite hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              );
            })}
          </div>
          </>
        )}
      </div>
      ) : (
        /* ===== Vue grille ===== */
        loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map((i) => <div key={i} className="h-64 bg-sand animate-pulse rounded-sm" />)}</div>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-no-products" className="p-10 text-center text-graphite bg-white border border-stone rounded-sm">Aucun produit ne correspond.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paged.map((p) => {
              const st = p.stock ?? 0;
              const isOut = st <= 0;
              const low = !isOut && st <= 3;
              const hidden = p.active === false;
              const checked = selected.includes(p.id);
              return (
                <div key={p.id} data-testid={`admin-product-card-${p.slug}`} className={`bg-white border rounded-sm overflow-hidden ${checked ? "border-gold ring-1 ring-gold" : "border-stone"} ${hidden ? "opacity-60" : ""}`}>
                  <div className="relative aspect-[4/3] bg-sand">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2"><Checkbox data-testid={`select-${p.slug}`} checked={checked} onCheckedChange={() => toggleSelect(p.id)} className="bg-white/90" /></div>
                    {p.featured && <Star className="absolute top-2 right-2 h-4 w-4 text-gold fill-gold" />}
                    <span className={`absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm ${isOut ? "bg-destructive text-white" : low ? "bg-amber-500 text-white" : "bg-cream/90 text-graphite"}`}>
                      {isOut ? "Rupture" : low ? `Faible · ${st}` : `Stock · ${st}`}
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-ink text-sm truncate">{p.name}</p>
                    <p className="text-[11px] text-ash truncate">{p.category} · {p.style}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-mono text-sm text-ink">{eur(p.price)}</span>
                      <span className={`text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm ${hidden ? "bg-stone text-ash" : "bg-emerald-50 text-emerald-700"}`}>{hidden ? "Hors vente" : "En vente"}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-stone">
                      <button type="button" data-testid={`stock-toggle-${p.slug}`} disabled={stockBusy === p.id} onClick={() => toggleStock(p)} className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${isOut ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-stone text-graphite hover:border-destructive hover:text-destructive"}`}>
                        {isOut ? <><PackageCheck className="h-3 w-3" /> Stock</> : <><PackageX className="h-3 w-3" /> Rupture</>}
                      </button>
                      <button data-testid={`duplicate-product-${p.slug}`} onClick={() => duplicateProduct(p)} title="Dupliquer" className="p-1.5 text-graphite hover:text-copper"><Copy className="h-3.5 w-3.5" /></button>
                      <button data-testid={`edit-product-${p.slug}`} onClick={() => openEdit(p)} className="p-1.5 text-graphite hover:text-ink"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`delete-product-${p.slug}`} onClick={() => setToDelete(p)} className="p-1.5 text-graphite hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Pagination */}
      {!loading && filtered.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-5" data-testid="products-pagination">
          <p className="text-xs text-ash">
            {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} sur {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="page-prev"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-2 border border-stone rounded-sm text-graphite hover:border-ink disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
              .reduce((acc, n) => {
                if (acc.length && n - acc[acc.length - 1] > 1) acc.push("…");
                acc.push(n);
                return acc;
              }, [])
              .map((n, idx) =>
                n === "…" ? (
                  <span key={`e${idx}`} className="px-2 text-ash text-xs">…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    data-testid={`page-${n}`}
                    onClick={() => setPage(n)}
                    className={`min-w-[34px] h-[34px] text-xs border rounded-sm transition-colors ${
                      page === n ? "bg-ink text-cream border-ink" : "border-stone text-graphite hover:border-ink"
                    }`}
                  >
                    {n}
                  </button>
                )
              )}
            <button
              type="button"
              data-testid="page-next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-2 border border-stone rounded-sm text-graphite hover:border-ink disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit dialog — enhanced */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
            <DialogDescription className="text-graphite text-sm">
              Renseignez toutes les informations utiles. La 1ère image est l&apos;image principale.
            </DialogDescription>
          </DialogHeader>

          <Tabs
            tabs={[
              { id: "general", label: "Général" },
              { id: "specs", label: "Caractéristiques" },
              { id: "media", label: "Médias" },
            ]}
            active={tab}
            onChange={setTab}
          />

          <form onSubmit={save} className="space-y-4">
            {/* ===== Onglet Général ===== */}
            {tab === "general" && (
              <>
                <div><Lbl>Nom</Lbl><Input data-testid="product-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                <div>
                  <Lbl>Description courte (résumé en haut de la fiche)</Lbl>
                  <Textarea data-testid="product-short-desc" value={form.short_description} onChange={set("short_description")} className="mt-1.5 rounded-sm border-stone bg-white" rows={2} placeholder="1–2 phrases d'accroche" />
                </div>
                <div>
                  <Lbl>Description complète</Lbl>
                  <Textarea data-testid="product-description" value={form.description} onChange={set("description")} className="mt-1.5 rounded-sm border-stone bg-white" rows={5} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div><Lbl>Prix actuel (€)</Lbl><Input data-testid="product-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                  <div><Lbl>Prix barré / initial (€)</Lbl><Input data-testid="product-original-price" type="number" step="0.01" value={form.original_price} onChange={set("original_price")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                  <div><Lbl>Stock</Lbl><Input data-testid="product-stock" type="number" value={form.stock} onChange={set("stock")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Lbl>Catégorie</Lbl>
                    <Select value={form.category_slug} onValueChange={(v) => setForm((f) => ({ ...f, category_slug: v }))}>
                      <SelectTrigger data-testid="product-category" className="mt-1.5 rounded-sm border-stone bg-white"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Lbl>Type</Lbl><Input data-testid="product-type" value={form.type} onChange={set("type")} placeholder="suspension, plafonnier…" className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                  <div><Lbl>Style</Lbl><Input data-testid="product-style" value={form.style} onChange={set("style")} placeholder="moderne, industriel…" className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Lbl>Marque</Lbl><Input data-testid="product-brand" value={form.brand} onChange={set("brand")} placeholder="Allemande…" className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                  <div><Lbl>SKU / Référence</Lbl><Input data-testid="product-sku" value={form.sku} onChange={set("sku")} placeholder="DL-12345" className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                  <div><Lbl>Poids (kg, optionnel)</Lbl><Input data-testid="product-weight" type="number" step="0.01" value={form.weight} onChange={set("weight")} placeholder="2.5" className="mt-1.5 rounded-sm border-stone bg-white" /></div>
                </div>
                <div>
                  <Lbl>Pièces / usages</Lbl>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ROOMS.map((r) => (
                      <button type="button" key={r} data-testid={`product-room-${r}`} onClick={() => toggleRoom(r)}
                        className={`text-xs px-3 py-1.5 border transition-colors ${form.rooms.includes(r) ? "bg-ink text-cream border-ink" : "border-stone text-graphite hover:border-ink"}`}>
                        {ROOM_LABELS[r]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between border border-stone rounded-sm p-3">
                    <Lbl>En vedette</Lbl>
                    <Switch data-testid="product-featured" checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} />
                  </div>
                  <div className="flex items-center justify-between border border-stone rounded-sm p-3">
                    <Lbl>En vente</Lbl>
                    <Switch data-testid="product-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
                  </div>
                </div>
              </>
            )}

            {/* ===== Onglet Caractéristiques ===== */}
            {tab === "specs" && (
              <>
                <p className="text-xs text-ash">
                  Champs facultatifs — remplissez ceux qui s&apos;appliquent à ce luminaire pour enrichir la fiche produit.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Lbl>Flux lumineux (lumens)</Lbl>
                    <Input data-testid="product-lumens" type="number" value={form.lumens} onChange={set("lumens")} placeholder="2600" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                  <div>
                    <Lbl>Teinte de lumière</Lbl>
                    <Select value={form.color_temp} onValueChange={(v) => setForm((f) => ({ ...f, color_temp: v }))}>
                      <SelectTrigger data-testid="product-color-temp" className="mt-1.5 rounded-sm border-stone bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {COLOR_TEMPS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Lbl>Puissance (W)</Lbl>
                    <Input data-testid="product-wattage" type="number" step="0.1" value={form.wattage} onChange={set("wattage")} placeholder="40" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                  <div>
                    <Lbl>Indice de protection (IP)</Lbl>
                    <Input data-testid="product-ip" value={form.ip_rating} onChange={set("ip_rating")} placeholder="IP20, IP44, IP65" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                  <div className="sm:col-span-2">
                    <Lbl>Dimensions</Lbl>
                    <Input data-testid="product-dimensions" value={form.dimensions} onChange={set("dimensions")} placeholder="Ø45 cm × H12 cm" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                  <div>
                    <Lbl>Matière</Lbl>
                    <Input data-testid="product-material" value={form.material} onChange={set("material")} placeholder="métal, verre…" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                  <div>
                    <Lbl>Finition</Lbl>
                    <Input data-testid="product-finish" value={form.finish} onChange={set("finish")} placeholder="nickel mat, chromé…" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                  <div className="sm:col-span-2">
                    <Lbl>Coloris disponibles (séparés par virgules)</Lbl>
                    <Input data-testid="product-colors" value={colorsInput} onChange={(e) => setColorsInput(e.target.value)} placeholder="gris, blanc, noir" className="mt-1.5 rounded-sm border-stone bg-white" />
                  </div>
                </div>
                <div className="flex items-center justify-between border border-stone rounded-sm p-3">
                  <div>
                    <Lbl>Variateur intégré</Lbl>
                    <p className="text-xs text-graphite mt-1">Intensité lumineuse réglable</p>
                  </div>
                  <Switch data-testid="product-dimmable" checked={form.dimmable} onCheckedChange={(v) => setForm((f) => ({ ...f, dimmable: v }))} />
                </div>
              </>
            )}

            {/* ===== Onglet Médias ===== */}
            {tab === "media" && (
              <div>
                <Lbl>Images du produit (la 1ère devient l&apos;image principale)</Lbl>
                <div className="mt-2">
                  <MultiImageUpload
                    testid="product-images"
                    value={form.images}
                    onChange={(imgs) => setForm((f) => ({ ...f, images: imgs, image: imgs[0] || f.image }))}
                    max={6}
                  />
                </div>
              </div>
            )}

            <DialogFooter className="!mt-6">
              <button type="submit" disabled={saving} data-testid="save-product-btn" className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer le produit"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-ink">Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.name} » sera définitivement retiré du catalogue.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent className="bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-ink">Supprimer {selected.length} produit{selected.length > 1 ? "s" : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive et retirera ces produits du catalogue.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-delete-btn" onClick={() => applyBulk("delete")} className="rounded-sm bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
